import { ListConversationsQuery, GetConversationInfosQuery } from '@graphql';
import { useFormik } from 'formik';
import * as Device from 'react-device-detect';

export * from './apolloError';
export * from './auth';
export * from './constants';
export * from './image';
export * from './booking';
export * from './spot';

export function debounce(fn: (...args: any[]) => any, delay: number) {
  let timeoutId: ReturnType<typeof setTimeout>;

  return (...args: any[]): Promise<any> => {
    return new Promise((resolve) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(async () => {
        const result = await fn(...args);
        resolve(result);
      }, delay);
    });
  };
}

export function truncateText(text: string, maxLength: number = 40): string {
  if (text.length > maxLength) {
    return text.slice(0, maxLength).trim() + '...';
  }
  return text;
}

export function getCountToText(number: number) {
  if (number < 1000) {
    return number;
  } else if (number < 1_000_000) {
    return `${Math.floor(number / 1000)}K`;
  } else if (number < 1_000_000_000) {
    return `${Math.floor(number / 1_000_000)}M`;
  } else if (number < 1_000_000_000_000) {
    return `${Math.floor(number / 1_000_000_000)}B`;
  }
}

export function getConversationTitle(
  accountId: string,
  conversation: ListConversationsQuery['conversations'][0] | GetConversationInfosQuery['conversation'],
  truncateTextLength = 25
) {
  let name = '';

  if (conversation.name) {
    name = conversation.name;
  } else {
    name =
      conversation.members.length > 2
        ? conversation.members
            .filter((m) => m.id !== accountId)
            .map((m) => m.name)
            .join(', ')
        : conversation.members.find((m) => m.id !== accountId)?.name || '';
  }

  return truncateText(name, truncateTextLength);
}

export function formatPriceRange(averageTicket?: number | null) {
  if (!averageTicket) {
    return 'NC';
  } else if (averageTicket < 20) {
    return 'cheap';
  } else if (averageTicket < 50) {
    return 'accessible';
  } else if (averageTicket < 200) {
    return 'high';
  } else {
    return 'premium';
  }
}

export function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function getTrueKeys(obj: Record<string, boolean | null | string>): string[] {
  return Object.keys(obj)
    .filter((key) => obj[key] === true)
    .filter((_, idx) => idx < 3);
}

export function getSpotTypes(obj: Record<string, boolean | null | string>) {
  return Object.keys(obj)
    .filter((key) => obj[key] === true)
    .filter((key) => !['__typename', 'id', 'spotId', 'accountId', 'updatedAt'].includes(key));
}

export function anonymizeEmail(email: string) {
  if (!email.includes('@')) {
    throw new Error('Invalid email');
  }

  // split the email into username and domain
  const [username, domain] = email.split('@');

  // hide part of the username and domain
  const anonymizedUsername =
    username.length > 2
      ? username[0] + '*'.repeat(username.length - 2) + username.slice(-1)
      : username[0] + '*';

  const [domainName, domainExtension] = domain.split('.');
  const anonymizedDomain = domainName[0] + '*'.repeat(domainName.length - 1) + '.' + domainExtension;

  return `${anonymizedUsername}@${anonymizedDomain}`;
}

export const handleLineBreak = (
  e: React.KeyboardEvent<HTMLDivElement>,
  content: string,
  submitForm: ReturnType<typeof useFormik>['submitForm']
) => {
  // if not Enter key, do nothing special
  if (e.key !== 'Enter') return;

  // if shift is pressed, keep default behavior
  if (e.shiftKey) {
    return;
  }

  // empty message guard
  if (content.trim().length === 0) {
    return;
  }

  if (Device.isDesktop) {
    e.preventDefault(); // ensure no line break is added
    submitForm();
  }
};

export const parseEmojis = (text: string) => {
  const emojiRegex = /\p{Emoji_Presentation}|\p{Emoji}\uFE0F/gu;
  const result = { isOnlyEmojis: false, count: 0 };

  // remove spaces
  const cleaned = text.trim();
  if (!cleaned) return result;

  // extract only emojis
  const emojis = cleaned.match(emojiRegex);
  if (!emojis) return result;

  // if all units of the string are emojis → match length === grapheme length
  const reconstructed = emojis.join('');

  result.isOnlyEmojis = reconstructed === cleaned;
  result.count = Array.from(reconstructed).length;

  return result;
};
